"use client";

import React from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, Area, AreaChart, Line, LineChart } from "recharts";
import { Droplets, ShieldCheck, Leaf } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const reductionData = [{
  year: "2020",
  volume: 100
}, {
  year: "2021",
  volume: 78
}, {
  year: "2022",
  volume: 55
}, {
  year: "2023",
  volume: 30
}];
const safetyData = [{
  phase: "Init",
  risk: 85
}, {
  phase: "Assess",
  risk: 60
}, {
  phase: "Mitigate",
  risk: 25
}, {
  phase: "Monitor",
  risk: 5
}];
const transformationData = [{
  stage: "Audit",
  score: 40
}, {
  stage: "Align",
  score: 70
}, {
  stage: "Adopt",
  score: 90
}, {
  stage: "Excel",
  score: 100
}];
const chartConfigReduction = {
  volume: {
    label: "Water Volume (%)",
    color: "var(--chart-2)"
  }
};
const chartConfigSafety = {
  risk: {
    label: "Risk Index",
    color: "var(--primary)"
  }
};
const chartConfigTransformation = {
  score: {
    label: "Compliance Score",
    color: "var(--chart-5)"
  }
};
export default function EngineeringSustainableFutures() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  return <section id="engineering-sustainable-futures" className="dark bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {}
        <div ref={headerReveal.ref} className={`max-w-3xl mx-auto text-center mb-16 lg:mb-24 transition-all duration-1000 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Engineering Sustainable Futures
          </h2>
          <div className="h-1.5 w-24 bg-primary mx-auto rounded-full mb-8" />
          <p className="text-lg text-muted-foreground leading-relaxed">At ATG Engineering and Consulting, we integrate rigorous geotechnical oversight with innovative environmental strategies. Our data-driven approach ensures compliance with global standards like GISTM and ICMM while actively reducing ecological footprints.</p>
        </div>

        {}
        <div ref={gridReveal.ref} className={`grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-1000 delay-200 ease-out ${gridReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <Card className="bg-card/50 border-border/50 backdrop-blur-sm flex flex-col" data-index={0}>
            <CardHeader>
              <div className="w-12 h-12 rounded-lg bg-chart-2/20 flex items-center justify-center mb-4">
                <Droplets className="w-6 h-6 text-chart-2" />
              </div>
              <CardTitle className="text-xl">Reduction</CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                Implementing advanced water management and balance strategies to
                significantly reduce fresh water intake and optimize tailings
                impoundment operations.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col justify-end mt-4">
              <div className="h-[200px] w-full mt-auto">
                <ChartContainer config={chartConfigReduction} className="h-full w-full">
                  <BarChart data={reductionData} margin={{
                  top: 10,
                  right: 10,
                  left: -20,
                  bottom: 0
                }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="year" tickLine={false} axisLine={false} />
                    <YAxis tickLine={false} axisLine={false} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="volume" fill="var(--color-volume)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  </BarChart>
                </ChartContainer>
              </div>
            </CardContent>
          </Card>

          {}
          <Card className="bg-card/50 border-border/50 backdrop-blur-sm flex flex-col" data-index={1}>
            <CardHeader>
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center mb-4">
                <ShieldCheck className="w-6 h-6 text-primary" />
              </div>
              <CardTitle className="text-xl">Safety</CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                Rigorous Engineer of Record (EOR) services and geotechnical
                inspections driving down operational risks and ensuring the
                structural integrity of earthwork impoundments.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col justify-end mt-4">
              <div className="h-[200px] w-full mt-auto">
                <ChartContainer config={chartConfigSafety} className="h-full w-full">
                  <AreaChart data={safetyData} margin={{
                  top: 10,
                  right: 10,
                  left: -20,
                  bottom: 0
                }}>
                    <defs>
                      <linearGradient id="fillRisk" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-risk)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--color-risk)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="phase" tickLine={false} axisLine={false} />
                    <YAxis tickLine={false} axisLine={false} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Area type="monotone" dataKey="risk" stroke="var(--color-risk)" fillOpacity={1} fill="url(#fillRisk)" strokeWidth={2} />
                  </AreaChart>
                </ChartContainer>
              </div>
            </CardContent>
          </Card>

          {}
          <Card className="bg-card/50 border-border/50 backdrop-blur-sm flex flex-col" data-index={2}>
            <CardHeader>
              <div className="w-12 h-12 rounded-lg bg-chart-5/20 flex items-center justify-center mb-4">
                <Leaf className="w-6 h-6 text-chart-5" />
              </div>
              <CardTitle className="text-xl">Transformation</CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                Guiding facilities through comprehensive program development to
                achieve full compliance with IRTB, GISTM, and ICMM standards for
                good tailings practices.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col justify-end mt-4">
              <div className="h-[200px] w-full mt-auto">
                <ChartContainer config={chartConfigTransformation} className="h-full w-full">
                  <LineChart data={transformationData} margin={{
                  top: 10,
                  right: 10,
                  left: -20,
                  bottom: 0
                }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="stage" tickLine={false} axisLine={false} />
                    <YAxis tickLine={false} axisLine={false} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line type="stepAfter" dataKey="score" stroke="var(--color-score)" strokeWidth={3} dot={{
                    r: 4,
                    fill: "var(--color-score)"
                  }} activeDot={{
                    r: 6
                  }} />
                  </LineChart>
                </ChartContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}